class QuickView2 extends HTMLElement {
  static current = null;

  constructor() {
    super();
    this.quickButton = this.querySelector('.quick') || this.closest('.quick');
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeyup = this.onKeyup.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onPanelGesture = this.onPanelGesture.bind(this);
  }

  connectedCallback() {
    this.card = this.closest('.card-container') || this.closest('.card');
    this.pictureWrapper = this.closest('.card__picture-wrapper');
    this.panel = this.pictureWrapper?.querySelector('[data-qv2-panel]');
    if (!this.panel) return;

    this.mini = !!this.closest('.quick2-mini');
    this.panel.classList.toggle('quick-view-2__modal--mini', this.mini);

    this.dialog = this.panel.querySelector('.quick-view-2__dialog');
    this.backdrop = this.panel.querySelector('[data-qv2-backdrop]');
    this.closeButton = this.panel.querySelector('[data-qv2-close]');
    this.submitButton = this.panel.querySelector('[data-qv2-submit]');
    this.submitSpinner = this.submitButton?.querySelector('.loading-overlay__spinner');
    this.headerPrice = this.panel.querySelector('[data-qv2-header-price]');
    this.submitPrice = this.panel.querySelector('[data-qv2-submit-price]');
    this.submitLabel = this.panel.querySelector('[data-qv2-submit-label]');
    this.placeholder = null;
    this.product = this.parseProduct();
    this.selected = this.getSelectedValues();
    this.cardLink = this.card?.querySelector('a.card') || this.closest('a.card');
    this.onCardClick = this.onCardClick.bind(this);
    this.onCardSwatchClick = this.onCardSwatchClick.bind(this);

    this.quickButton?.addEventListener('click', this.onTriggerClick);
    this.card?.addEventListener('click', this.onCardSwatchClick);
    this.closeButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
    this.backdrop?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
    this.panel.addEventListener('click', this.onPanelClick.bind(this));
    this.panel.addEventListener('mousedown', this.onPanelGesture);
    this.panel.addEventListener('mouseup', this.onPanelGesture);
    this.submitButton?.addEventListener('click', this.onSubmit);
    this.syncUi(false);
  }

  disconnectedCallback() {
    this.quickButton?.removeEventListener('click', this.onTriggerClick);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keyup', this.onKeyup);
    window.removeEventListener('resize', this.onResize);
    this.cardLink?.removeEventListener('click', this.onCardClick, true);
    this.card?.removeEventListener('click', this.onCardSwatchClick);
    this.panel?.removeEventListener('mousedown', this.onPanelGesture);
    this.panel?.removeEventListener('mouseup', this.onPanelGesture);
    if (QuickView2.current === this) QuickView2.current = null;
    clearTimeout(this.closeTimer);
    this.restorePanel();
  }

  parseProduct() {
    const node = this.panel.querySelector('[data-qv2-product]');
    if (!node) return { variants: [], optionsSize: 0 };
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Quick view 2: invalid product JSON', error);
      return { variants: [], optionsSize: 0 };
    }
  }

  isMobile() {
    return window.matchMedia(this.mini ? '(max-width: 1499px)' : '(max-width: 768px)').matches;
  }

  onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.panel.classList.contains('is-open')) {
      this.close();
      return;
    }
    this.open();
  }

  open() {
    if (QuickView2.current && QuickView2.current !== this) {
      QuickView2.current.close(true);
    }

    this.closing = false;
    clearTimeout(this.closeTimer);
    this.panel.hidden = false;
    this.panel.classList.remove('is-open', 'is-closing');
    this.dialog?.setAttribute('aria-modal', this.isMobile() ? 'true' : 'false');
    this.movePanelForViewport();
    this.syncFromCardSwatch();
    QuickView2.current = this;
    this.cardLink?.addEventListener('click', this.onCardClick, true);

    if (this.isMobile()) {
      document.body.classList.add('hidden', 'quick-view-2-open');
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.closing) return;
        this.panel.classList.add('is-open');
      });
    });

    window.addEventListener('resize', this.onResize);
    document.addEventListener('keyup', this.onKeyup);
    setTimeout(() => document.addEventListener('click', this.onDocumentClick), 0);
  }

  close(immediate) {
    if (this.closing || (this.panel.hidden && !this.panel.classList.contains('is-open'))) return;

    this.closing = true;
    this.panel.classList.add('is-closing');
    this.panel.classList.remove('is-open');
    this.dialog?.setAttribute('aria-modal', 'false');
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keyup', this.onKeyup);
    window.removeEventListener('resize', this.onResize);
    this.cardLink?.removeEventListener('click', this.onCardClick, true);

    let onEnd = null;
    const finish = () => {
      if (!this.closing) return;
      this.closing = false;
      clearTimeout(this.closeTimer);
      if (onEnd) this.dialog?.removeEventListener('transitionend', onEnd);
      this.panel.classList.remove('is-closing');
      this.panel.hidden = true;
      this.restorePanel();
      document.body.classList.remove('quick-view-2-open');
      if (!document.querySelector('quick-view-drawer[open], details[open] .quick-view')) {
        document.body.classList.remove('hidden');
      }
      if (QuickView2.current === this) QuickView2.current = null;
    };

    if (immediate) {
      finish();
      return;
    }

    onEnd = (event) => {
      if (event.target !== this.dialog) return;
      finish();
    };
    this.dialog?.addEventListener('transitionend', onEnd);
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(finish, 400);
  }

  onCardClick(event) {
    if (this.panel.contains(event.target) || this.contains(event.target)) return;
    if (event.target.closest('.card__colors')) return;
    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  onCardSwatchClick(event) {
    const swatch = event.target.closest('color-swatch');
    if (!swatch || !this.card.contains(swatch) || !swatch.closest('.card__colors')) return;
    this.syncFromCardSwatch(swatch);
  }

  syncFromCardSwatch(swatch) {
    const active =
      swatch?.classList.contains('active-swatch')
        ? swatch
        : this.card?.querySelector('.card__colors .color-swatch.active-swatch');
    if (!active) return;

    const colorFieldset = this.panel.querySelector('.quick-view-2__fieldset--color');
    if (!colorFieldset) return;

    const colorValue = this.getColorValueFromCardSwatch(active, colorFieldset);
    if (!colorValue) return;

    const button = Array.from(colorFieldset.querySelectorAll('[data-qv2-input]')).find(
      (el) => el.getAttribute('data-option-value') === colorValue
    );
    if (!button || button.classList.contains('is-selected')) return;

    this.selectOption(button, false);
  }

  getColorValueFromCardSwatch(swatch, colorFieldset) {
    const title = (swatch.getAttribute('title') || '').trim();
    const buttons = Array.from(colorFieldset.querySelectorAll('[data-qv2-input]'));
    if (title && buttons.some((el) => el.getAttribute('data-option-value') === title)) {
      return title;
    }

    const variantId = swatch.dataset.variantId;
    if (!variantId || !this.product?.variants) return title || '';

    const variant = this.product.variants.find((item) => String(item.id) === String(variantId));
    const colorIndex = Number(colorFieldset.getAttribute('data-option-index'));
    if (!variant || Number.isNaN(colorIndex)) return title || '';
    return variant.options[colorIndex] || title || '';
  }

  movePanelForViewport() {
    if (this.isMobile()) {
      if (!this.placeholder) {
        this.placeholder = document.createComment('quick-view-2');
        this.panel.before(this.placeholder);
        document.body.append(this.panel);
      }
      return;
    }
    this.restorePanel();
  }

  restorePanel() {
    if (this.placeholder && this.placeholder.parentNode) {
      this.placeholder.before(this.panel);
      this.placeholder.remove();
      this.placeholder = null;
    }
  }

  onResize() {
    if (!this.panel.classList.contains('is-open')) return;
    this.movePanelForViewport();
    if (this.isMobile()) {
      document.body.classList.add('hidden', 'quick-view-2-open');
    } else {
      document.body.classList.remove('quick-view-2-open');
      if (!document.querySelector('.quick-view-open')) {
        document.body.classList.remove('hidden');
      }
    }
  }

  onDocumentClick(event) {
    if (this.contains(event.target) || this.panel.contains(event.target)) return;
    if (this.card?.contains(event.target) && event.target.closest('.card__colors')) return;
    this.close();
  }

  onKeyup(event) {
    if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
  }

  getSelectedValues() {
    return Array.from(this.panel.querySelectorAll('[data-qv2-option]')).map((fieldset) => {
      const selected = fieldset.querySelector('[data-qv2-input].is-selected');
      return selected ? selected.getAttribute('data-option-value') || '' : '';
    });
  }

  onPanelGesture(event) {
    event.stopPropagation();
  }

  onPanelClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const option = event.target.closest('[data-qv2-input]');
    if (!option || !this.panel.contains(option)) return;
    this.selectOption(option);
  }

  selectOption(button, updateCard = true) {
    const fieldset = button.closest('[data-qv2-option]');
    if (!fieldset) return;

    fieldset.querySelectorAll('[data-qv2-input]').forEach((el) => {
      el.classList.remove('is-selected');
      el.setAttribute('aria-pressed', 'false');
    });
    button.classList.add('is-selected');
    button.setAttribute('aria-pressed', 'true');

    this.selected = this.getSelectedValues();
    this.syncUi(updateCard);
  }

  findVariant() {
    const selected = this.selected;
    if (!this.product?.variants?.length) return null;
    if (!selected.length) {
      return this.product.variants.find((variant) => variant.available) || this.product.variants[0];
    }
    return this.product.variants.find((variant) =>
      selected.every((value, index) => variant.options[index] === value)
    ) || null;
  }

  isValueAvailable(optionIndex, value) {
    return this.product.variants.some((variant) => {
      if (variant.options[optionIndex] !== value) return false;
      return this.selected.every((selectedValue, index) => {
        if (index === optionIndex) return true;
        return variant.options[index] === selectedValue;
      }) && variant.available;
    });
  }

  syncUi(updateCard = false) {
    const variant = this.findVariant();
    this.panel.querySelectorAll('[data-qv2-option]').forEach((fieldset, optionIndex) => {
      const selected = fieldset.querySelector('[data-qv2-input].is-selected');
      const label = fieldset.querySelector('[data-qv2-option-label]');
      if (label && selected) label.textContent = selected.getAttribute('data-option-value') || '';

      fieldset.querySelectorAll('.quick-view-2__option').forEach((optionEl) => {
        const value = optionEl.getAttribute('data-option-value');
        const available = this.isValueAvailable(optionIndex, value);
        optionEl.classList.toggle('quick-view-2__option--unavailable', !available);
      });
    });

    this.renderPrice(this.headerPrice, variant);
    this.renderPrice(this.submitPrice, variant, true);
    this.updateSubmit(variant);
    if (updateCard) this.updateCard(variant);
  }

  renderPrice(container, variant, compact) {
    if (!container) return;
    if (!variant) {
      container.innerHTML = '';
      return;
    }

    const compare =
      variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<s>${variant.compare_at_formatted}</s>`
        : '';
    const saleClass = compare ? ' class="sale-price"' : '';
    container.innerHTML = `<span${saleClass}>${variant.price_formatted}</span>${compact ? ' ' : ''}${compare}`;
  }

  updateSubmit(variant) {
    if (!this.submitButton) return;
    if (this.submitButton.dataset.comingSoon === 'true') {
      this.submitButton.disabled = true;
      if (this.submitLabel) this.submitLabel.textContent = this.submitButton.dataset.labelComingSoon || '';
      return;
    }
    const addLabel = this.submitButton.dataset.labelAdd || '';
    const soldOutLabel = this.submitButton.dataset.labelSoldOut || '';

    if (variant && variant.available) {
      this.submitButton.disabled = false;
      if (this.submitLabel) this.submitLabel.textContent = addLabel;
      return;
    }

    this.submitButton.disabled = true;
    if (this.submitLabel) this.submitLabel.textContent = soldOutLabel;
  }

  updateCard(variant) {
    if (!variant || !this.card) return;
    if (variant.featured_image) {
      const images = this.card.querySelectorAll('.card__picture-wrapper img.card__image');
      images.forEach((img, index) => {
        if (index > 0 && img.classList.contains('card__image--second')) return;
        img.src = variant.featured_image;
        img.removeAttribute('srcset');
      });
    }

    const priceRoot = this.card.querySelector('.card__price .price__wrapper') || this.card.querySelector('.price__wrapper');
    if (!priceRoot) return;
    const sale = priceRoot.querySelector('.sale-price');
    const regular = priceRoot.querySelector('.regular-price');
    const regularWrap = regular?.closest('.price__regular');
    if (sale) {
      sale.textContent = variant.price_formatted;
      if (regular && regularWrap) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          regular.textContent = variant.compare_at_formatted;
          regularWrap.style.display = '';
        } else {
          regularWrap.style.display = 'none';
        }
      }
      return;
    }
    if (regular) regular.textContent = variant.price_formatted;
  }

  async onSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    const variant = this.findVariant();
    if (this.submitButton?.dataset.comingSoon === 'true') return;
    if (!variant?.available || this.submitButton.disabled) return;

    this.submitButton.classList.add('loading');
    this.submitButton.setAttribute('disabled', 'true');
    this.submitSpinner?.classList.remove('hidden');

    const miniCart =
      document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const sections = miniCart
      ? miniCart.getSectionsToRender().map((section) => section.id)
      : [];

    try {
      const response = await fetch(`${window.routes.cart_add_url}`, {
        ...fetchConfig('javascript'),
        body: JSON.stringify({
          id: variant.id,
          quantity: 1,
          sections,
          sections_url: window.location.pathname
        })
      });
      const parsedState = await response.json();

      if (parsedState.status === 422) {
        document.dispatchEvent(
          new CustomEvent('ajaxProduct:error', {
            detail: { errorMessage: parsedState.description }
          })
        );
        return;
      }

      if (miniCart) miniCart.renderContents(parsedState);
      document.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          detail: { product: parsedState }
        })
      );
      this.close();
    } catch (error) {
      console.error(error);
    } finally {
      this.submitButton.classList.remove('loading');
      this.submitSpinner?.classList.add('hidden');
      this.submitButton.removeAttribute('disabled');
      this.syncUi();
    }
  }
}

customElements.define('quick-view-2', QuickView2);
